
import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Search, CheckCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import PaymentStatusBadge from '@/components/PaymentStatusBadge'

const OperatorStudents = () => {
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [operatorInfo, setOperatorInfo] = useState<any>(null)
  const { toast } = useToast()

  const fetchOperatorInfo = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      const { data: operator } = await supabase
        .from('operators')
        .select('*')
        .eq('user_id', session.user.id)
        .single()
      
      setOperatorInfo(operator)
    }
  }

  const fetchStudents = async () => {
    try {
      console.log('Fetching students for school_id:', operatorInfo.school_id)
      
      // Get all operators to exclude them from the student list
      const { data: allOperators } = await supabase
        .from('operators')
        .select('user_id')

      const operatorUserIds = allOperators?.map(op => op.user_id) || []
      console.log('Operator user IDs to exclude:', operatorUserIds)
      
      // Get students for this specific school (excluding operators)
      // Only filter by school_id, not email domain
      const { data: studentsData, error } = await supabase
        .from('profiles')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .eq('school_id', operatorInfo.school_id)
        .not('id', 'in', `(${operatorUserIds.join(',')})`)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching students:', error)
        throw error
      }

      console.log(`Found ${studentsData?.length || 0} students for school ${operatorInfo.school_id}`)
      console.log('Students data:', studentsData)
      
      setStudents(studentsData || [])
    } catch (error: any) {
      console.error('Error fetching students:', error)
      toast({
        title: "Error fetching students",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleManualPaymentOverride = async (studentId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          payment_status: 'paid',
          payment_completed_at: new Date().toISOString()
        })
        .eq('id', studentId)
      
      if (error) throw error
      
      toast({
        title: "Payment status updated",
        description: "Student has been marked as paid.",
      })
      
      // Refresh the student list
      fetchStudents()
    } catch (error: any) {
      toast({
        title: "Error updating payment status",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const filteredStudents = students.filter(student => 
    student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.class?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  useEffect(() => {
    fetchOperatorInfo()
  }, [])

  useEffect(() => {
    if (operatorInfo) {
      fetchStudents()
    }
  }, [operatorInfo])

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Students</CardTitle>
          <CardDescription>Loading students...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Students</CardTitle>
            <CardDescription>View students enrolled in your school</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-64"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Points</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Unlock Code</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStudents.map((student: any) => (
              <TableRow key={student.id}>
                <TableCell className="font-medium">
                  {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'N/A'}
                </TableCell>
                <TableCell>{student.email || 'N/A'}</TableCell>
                <TableCell>{student.schools?.name || 'N/A'}</TableCell>
                <TableCell>{student.class || 'N/A'}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{student.level || 1}</Badge>
                </TableCell>
                <TableCell>{student.points || 0}</TableCell>
                <TableCell>
                  <PaymentStatusBadge status={student.payment_status || 'free'} />
                </TableCell>
                <TableCell>
                  {student.assigned_unlock_code ? (
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {student.assigned_unlock_code}
                    </code>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  {student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  {student.payment_status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleManualPaymentOverride(student.id)}
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="h-3 w-3" />
                      Mark Paid
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredStudents.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No students found matching your search.' : 'No students enrolled yet.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export default OperatorStudents
